import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import logger from '../utils/logger';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);

    const checkingRef = useRef(false);

  const checkAuth = useCallback(async () => {
    // Prevent concurrent calls
    if (checkingRef.current) {
      logger.debug('[AuthContext] checkAuth already in progress, skipping');
      return;
    }
    checkingRef.current = true;
    
    logger.debug('[AuthContext] checkAuth started');
    
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes('session_id=')) {
      logger.debug('[AuthContext] OAuth callback detected, skipping auth check');
      checkingRef.current = false;
      setLoading(false);
      return;
    }

    try {
      // Try standard JWT auth only - simplest approach
      logger.debug('[AuthContext] Trying /auth/me...');
      const { data } = await api.get('/auth/me', { timeout: 5000 });
      logger.debug('[AuthContext] Auth success:', data?.email);
      setUser(data);
      
      if (data && !data.role && (data.authProvider === 'google' || data.authProvider === 'apple')) {
        setNeedsProfileCompletion(true);
      }
    } catch (error) {
      logger.debug('[AuthContext] Auth failed:', error?.response?.status || error?.message);
      // User is not authenticated - that's OK for login/register pages
      setUser(null);
    } finally {
      logger.debug('[AuthContext] checkAuth complete');
      checkingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post(
      '/auth/login',
      { email, password },
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const register = async (userData) => {
    const { data } = await api.post(
      '/auth/register',
      userData,
      { withCredentials: true }
    );
    setUser(data);
    return data;
  };

  const logout = async () => {
    // Also try Google logout
    try {
      await api.post(`/auth/google/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore if Google logout fails
    }
    // Also try Apple logout
    try {
      await api.post(`/auth/apple/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore if Apple logout fails
    }
    await api.post(`/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    setNeedsProfileCompletion(false);
  };

  // Google OAuth login - redirects to Emergent Auth
  // REMINDER: Pour Capacitor natif, on doit utiliser l'URL de production, pas window.location.origin
  const loginWithGoogle = () => {
    // En mode natif Capacitor, window.location.origin ne fonctionne pas (retourne capacitor://localhost)
    // On utilise donc l'URL de production fixe
    const productionUrl = process.env.REACT_APP_BACKEND_URL || 'https://xoclub.app';
    const redirectUrl = productionUrl + '/';
    const authBaseUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    window.location.href = `${authBaseUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Apple OAuth login - uses Apple Sign-In JS SDK
  const loginWithApple = async () => {
    if (!window.AppleID) {
      throw new Error('Apple Sign-In SDK not loaded');
    }

    try {
      // Initialize Apple Sign-In avec redirectURI pour le mode popup
      const redirectURI = `${window.location.origin}/auth/apple/callback`;
      
      window.AppleID.auth.init({
        clientId: 'app.xoclub.web',
        scope: 'name email',
        redirectURI: redirectURI,
        usePopup: true
      });

      // Trigger Apple Sign-In popup
      const response = await window.AppleID.auth.signIn();

      // Extract data from response
      const { authorization, user: appleUser } = response;

      if (!authorization || !authorization.id_token) {
        throw new Error('No authorization data received from Apple');
      }

      // Send to backend for verification
      const backendResponse = await api.post(
        '/auth/apple/callback',
        {
          id_token: authorization.id_token,
          user: appleUser || null
        },
        { withCredentials: true }
      );

      const userData = backendResponse.data;
      
      if (userData.success && userData.user) {
        setUser(userData.user);
        
        // Check if new user needs profile completion
        if (userData.needsProfileCompletion) {
          setNeedsProfileCompletion(true);
        }
      }

      return userData;
    } catch (error) {
      // Check if user closed the popup
      if (error.error === 'popup_closed_by_user') {
        const cancelError = new Error('popup_closed_by_user');
        cancelError.error = 'popup_closed_by_user';
        throw cancelError;
      }
      throw error;
    }
  };

  // Complete profile after Google/Apple login
  const completeProfile = (userData) => {
    setUser(userData);
    setNeedsProfileCompletion(false);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    loginWithGoogle,
    loginWithApple,
    needsProfileCompletion,
    setNeedsProfileCompletion,
    completeProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
